import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { isEffortAxis, type EffortScale, type IntensityKind, type UnitsPreference } from "@heraion/shared";

import { Chip } from "../../primitives/Chip";
import type { UiTheme } from "../../theme";
import { useKindLabels } from "../useKindLabels";
import { makeLoadAxisSelectorStyles } from "./loadAxisSelectorStyles";

// The load axis is mutually exclusive (SetIntensity is a discriminated union),
// so one radiogroup. The effort chip speaks the athlete's scale and stands for
// both RPE and RIR: a set on the other one stays as it is (#432). «No
// intensity» is the last chip.
const LOAD_KINDS: readonly IntensityKind[] = ["absolute", "percent-of-max", "history"];

export type LoadAxisSelectorProps = {
  theme: UiTheme;
  /** The sets' axis. null: mixed axes, no chip selected. */
  kind: IntensityKind | null;
  /** The athlete's effort scale: the effort chip's label and what it writes. */
  scale: EffortScale;
  /** The effort kind the round's sets all share: the chip is NAMED after it, so
   *  it never says RIR above sets that read RPE. Unset (empty or mixed round):
   *  the chip speaks `scale`. Tapping writes `scale` either way (#432). */
  shownEffort?: EffortScale;
  onSelect: (kind: IntensityKind) => void;
  /** The unit the DRAFT cells are expressed in (composer.cellUnit) — passed
   *  straight through to useKindLabels so the "kg"/"LB" chip label matches
   *  what the metric table and day-panel already show.
   *  ⛔ Required (CN-26 wave 3): see useKindLabels. */
  cellUnit: UnitsPreference;
};

// PE-22 K3.2 — «Cosa determina il carico»: prescribe-only. Clones the
// radiogroup-of-Chip pattern at ComposerSetRow.tsx:216-247 (the BOUNDS work-
// axis selector) rather than inventing a SegmentedControl or reaching for the
// Select dropdown — the consolidation scout's call.
//
// Review fix (PE-22 K3 triad, Critical): the four axis labels used to live
// here as their own `mobile.logger.loadAxis*` keys, duplicating the
// intensity-kind label resolver useKindLabels.ts already owns as "the ONE
// place" (see its own header — the PE-14 English-leak bug came from exactly
// this kind of bypass). Routing through useKindLabels here closes that gap.
export function LoadAxisSelector({
  theme,
  kind,
  scale,
  shownEffort,
  onSelect,
  cellUnit,
}: LoadAxisSelectorProps) {
  const { t } = useTranslation();
  const styles = makeLoadAxisSelectorStyles(theme);
  const kindLabels = useKindLabels(cellUnit);
  const label = t("mobile.logger.loadAxisTitle", {
    defaultValue: "What determines the load",
  });
  const chips: ReadonlyArray<{ kind: IntensityKind; label: string; selected: boolean }> = [
    ...LOAD_KINDS.map((k) => ({ kind: k, label: kindLabels[k], selected: kind === k })),
    { kind: scale, label: kindLabels[shownEffort ?? scale], selected: kind !== null && isEffortAxis(kind) },
    {
      kind: "none",
      label: t("mobile.logger.loadAxisNone", { defaultValue: "No intensity" }),
      selected: kind === "none",
    },
  ];

  return (
    <View style={styles.line}>
      <Text style={styles.label}>{label}</Text>
      <View
        accessibilityRole="radiogroup"
        accessibilityLabel={label}
        style={styles.row}
      >
        {chips.map((c) => (
          <Chip
            key={c.kind}
            theme={theme}
            role="radio"
            label={c.label}
            selected={c.selected}
            onPress={() => onSelect(c.kind)}
          />
        ))}
      </View>
    </View>
  );
}
